import { clamp } from 'lodash-es';
import { useCallback, useRef, useState } from 'react';

/** Minimum gap, in pixels, between the window and the viewport edges in its initial centred placement. */
const VIEWPORT_MARGIN = 80;
/** Smallest width/height, in pixels, the window can be resized to. */
const MIN_SIZE = 160;
/** Minimum width of the title bar, in pixels, that must remain within the viewport horizontally. */
const MIN_HORIZONTAL_VISIBLE = 30;

/**
 * Manages position and size for a floating, draggable, resizable window.
 *
 * @returns
 * - bounds: current `{ left, top, width, height }` in pixels.
 * - titleBarRef: ref to attach to the title bar element; its measured height drives the visibility constraint.
 * - moveHandleProps: pointer handlers to spread onto the drag (title bar) element.
 * - getResizeHandleProps: returns pointer handlers to spread onto an edge/corner handle for the given direction.
 */
export function useFloatingWindow() {
	const [bounds, setBounds] = useState<FloatingWindowBounds>(getInitialBounds);
	const dragState = useRef<DragState | null>(null);
	const titleBarRef = useRef<HTMLDivElement | null>(null);

	const handlePointerDown = useCallback(
		(mode: 'move' | 'resize', direction: ResizeDirection) => (event: React.PointerEvent<HTMLElement>) => {
			event.preventDefault();
			event.currentTarget.setPointerCapture(event.pointerId);
			dragState.current = {
				mode,
				direction,
				pointerId: event.pointerId,
				startPointer: { x: event.clientX, y: event.clientY },
				startBounds: bounds,
			};
		},
		[bounds],
	);

	const handlePointerMove: React.PointerEventHandler<HTMLElement> = useCallback((event) => {
		const state = dragState.current;
		if (!state || state.pointerId !== event.pointerId) {
			return;
		}

		const deltaX = event.clientX - state.startPointer.x;
		const deltaY = event.clientY - state.startPointer.y;
		const titleBarHeight = titleBarRef.current?.offsetHeight ?? 0;

		if (state.mode === 'move') {
			const moved = {
				...state.startBounds,
				left: state.startBounds.left + deltaX,
				top: state.startBounds.top + deltaY,
			};
			setBounds(clampToVisibility(moved, titleBarHeight));
		} else {
			setBounds(resizeBounds(state.startBounds, state.direction, deltaX, deltaY, titleBarHeight));
		}
	}, []);

	const handlePointerUp: React.PointerEventHandler<HTMLElement> = useCallback((event) => {
		if (dragState.current?.pointerId === event.pointerId) {
			event.currentTarget.releasePointerCapture(event.pointerId);
			dragState.current = null;
		}
	}, []);

	const getResizeHandleProps = useCallback(
		(direction: ResizeDirection) => ({
			onPointerDown: handlePointerDown('resize', direction),
			onPointerMove: handlePointerMove,
			onPointerUp: handlePointerUp,
			onPointerCancel: handlePointerUp,
		}),
		[handlePointerDown, handlePointerMove, handlePointerUp],
	);

	return {
		bounds,
		titleBarRef,
		moveHandleProps: {
			onPointerDown: handlePointerDown('move', 'se'),
			onPointerMove: handlePointerMove,
			onPointerUp: handlePointerUp,
			onPointerCancel: handlePointerUp,
		},
		getResizeHandleProps,
	};
}

export interface FloatingWindowBounds {
	left: number;
	top: number;
	width: number;
	height: number;
}

/** The edge(s) being dragged: a single edge or a corner (two edges combined). */
export type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface DragState {
	mode: 'move' | 'resize';
	/** Edges being dragged; only meaningful when `mode` is `'resize'`. */
	direction: ResizeDirection;
	pointerId: number;
	startPointer: { x: number; y: number };
	startBounds: FloatingWindowBounds;
}

/** Computes a centred initial window placement that fits within the current viewport. */
function getInitialBounds(): FloatingWindowBounds {
	const width = Math.min(1100, window.innerWidth * 0.9);
	const height = Math.min(780, window.innerHeight * 0.85);
	return {
		width,
		height,
		// Prefer the margin, but never let it push the far edge off-screen on small viewports.
		left: Math.min(window.innerWidth - width, Math.max(VIEWPORT_MARGIN, (window.innerWidth - width) / 2)),
		top: Math.min(window.innerHeight - height, Math.max(VIEWPORT_MARGIN, (window.innerHeight - height) / 2)),
	};
}

/**
 * Clamps `bounds` so the title bar stays reachable: at least {@link MIN_HORIZONTAL_VISIBLE} px remain
 * visible horizontally and the title bar's full height remains visible vertically.
 *
 * @param bounds - Candidate bounds to constrain.
 * @param titleBarHeight - Measured title bar height, in pixels.
 * @returns Bounds with `left`/`top` clamped into the visible range.
 */
function clampToVisibility(bounds: FloatingWindowBounds, titleBarHeight: number): FloatingWindowBounds {
	return {
		...bounds,
		left: clamp(bounds.left, MIN_HORIZONTAL_VISIBLE - bounds.width, window.innerWidth - MIN_HORIZONTAL_VISIBLE),
		top: clamp(bounds.top, 0, window.innerHeight - titleBarHeight),
	};
}

/**
 * Resizes `startBounds` by the given pointer delta.
 *
 * @param startBounds - Bounds captured when the resize drag began.
 * @param direction - Which edge(s) the pointer is dragging.
 * @param deltaX - Horizontal pointer movement since drag start, in pixels.
 * @param deltaY - Vertical pointer movement since drag start, in pixels.
 * @param titleBarHeight - Measured title bar height, in pixels.
 * @returns The new bounds.
 */
function resizeBounds(
	startBounds: FloatingWindowBounds,
	direction: ResizeDirection,
	deltaX: number,
	deltaY: number,
	titleBarHeight: number,
): FloatingWindowBounds {
	let { left, top, width, height } = startBounds;

	if (direction.includes('e')) {
		// Right edge may extend past the viewport's right edge, but not so far left that the title bar's
		// visible width drops below the minimum (only relevant when the left edge is already off-screen).
		const minWidth = Math.max(MIN_SIZE, MIN_HORIZONTAL_VISIBLE - startBounds.left);
		width = Math.max(minWidth, startBounds.width + deltaX);
	} else if (direction.includes('w')) {
		// Left edge may extend past the viewport's left edge, but not so far right that the title bar's
		// visible width drops below the minimum (only relevant when the right edge is already off-screen).
		const right = startBounds.left + startBounds.width;
		const maxLeft = Math.min(right - MIN_SIZE, window.innerWidth - MIN_HORIZONTAL_VISIBLE);
		left = Math.min(startBounds.left + deltaX, maxLeft);
		width = right - left;
	}

	if (direction.includes('s')) {
		// Bottom edge may extend past the viewport; the (fixed) title bar at the top stays visible.
		height = Math.max(MIN_SIZE, startBounds.height + deltaY);
	} else if (direction.includes('n')) {
		// Top edge cannot rise above the viewport (title bar would be clipped) nor drop so far that the
		// title bar falls below the viewport's bottom.
		const bottom = startBounds.top + startBounds.height;
		const maxTop = Math.min(bottom - MIN_SIZE, window.innerHeight - titleBarHeight);
		top = clamp(startBounds.top + deltaY, 0, maxTop);
		height = bottom - top;
	}

	return { left, top, width, height };
}
